import { Suspense, useContext, useEffect, useMemo, useRef, useState } from "react"
import { BrowserRouter } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { useLanguages } from "lang/languages"
import MobileApp from "mobile/App"
import { OverlayContext } from "modules/overlays"
import { useAuth, UserContext } from "modules/users"
import DialogOverlay from "views/Overlay/DialogOverlay"
import LoadingOverlay from "views/Overlay/LoadingOverlay"

const SuspensePlaceholder = () => {
  const { loadingRef } = useContext(OverlayContext)

  useEffect(() => loadingRef.current.open(), [loadingRef])

  return null
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (_, { status }) => {
        if (status === 401) {
          return false
        } else {
          return 3
        }
      },
    },
  },
})

const App = () => {
  const dialogRef = useRef()
  const loadingRef = useRef()
  const actionBarRef = useRef()
  const { fetchUser, getAuthURL } = useAuth()
  const { language } = useLanguages()
  const { host, pathname } = window.location
  const [user, setUser] = useState(null)

  const userContext = useMemo(() => ({ user, setUser }), [user])
  const overlayContext = useMemo(() => ({ loadingRef, dialogRef, actionBarRef }), [])

  useEffect(() => {
    if (!pathname.includes("customer")) {
      const finishCallback = loadingRef.current.open()
      fetchUser()
        .then(setUser)
        .catch((error) => {
          if (error?.status === 401) {
            setUser(null)
            getAuthURL().then((url) => {
              window.location.assign(url)
            })
          } else {
            dialogRef.current.open({
              type: "error",
              message: {
                name: "帳號資訊取得失敗",
                title: "請稍候再試",
                content: error,
              },
            })
          }
        })
        .finally(finishCallback)
    }
  }, [fetchUser, host, pathname, getAuthURL])

  useEffect(() => {
    document.documentElement.setAttribute("lang", language)
  }, [language])

  return (
    <QueryClientProvider client={queryClient}>
      <OverlayContext.Provider value={overlayContext}>
        <DialogOverlay ref={dialogRef} />
        <LoadingOverlay ref={loadingRef} />
        <UserContext.Provider value={userContext}>
          <Suspense fallback={<SuspensePlaceholder />}>
            {(user || pathname.includes("customer")) && (
              <BrowserRouter>
                <MobileApp />
              </BrowserRouter>
            )}
          </Suspense>
        </UserContext.Provider>
      </OverlayContext.Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
